import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { handleGoogleSession } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.replace('#', ''));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          toast.error('No session ID found');
          navigate('/login');
          return;
        }

        // Exchange session_id for user data
        const user = await handleGoogleSession(sessionId);
        
        // Clear the URL hash
        window.history.replaceState(null, '', window.location.pathname);
        
        toast.success(`Welcome, ${user.name}!`);
        
        // Return to the page user was on before clicking login (if any)
        const stored = sessionStorage.getItem('postLoginRedirect');
        if (stored) {
          sessionStorage.removeItem('postLoginRedirect');
          navigate(stored, { replace: true });
          return;
        }
        
        // Fallback: admins go to admin dashboard, everyone else to home
        if (user.role === 'admin') {
          navigate('/admin', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      } catch (error) {
        console.error('Google auth error:', error);
        toast.error('Failed to sign in with Google. Please try again.');
        navigate('/login', { replace: true });
      }
    };

    processSession();
  }, [handleGoogleSession, navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center" data-testid="auth-callback">
      <div className="text-center">
        <div className="spinner mx-auto mb-4" style={{ width: '40px', height: '40px' }}></div>
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
}
